import { useRef, useState } from 'react';

import {
  ArrowDown01Icon,
  CustomerSupportIcon,
  Delete01Icon,
  EyeIcon,
  ShieldKeyIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { Permissions } from 'constants/permissions';
import { useClickOutside } from 'hooks/click-outside';
import moment from 'moment';
import { useSelector } from 'react-redux';

import Alert from 'components/Alert/Alert';
import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';

import {
  useDenySupportAccessRequestMutation,
  useGetActiveSupportAccessRequestsQuery,
  useGetPendingSupportAccessRequestsQuery,
  useGrantSupportAccessRequestMutation,
  useRevokeSupportAccessRequestMutation,
} from 'api/support-access-api';
import { selectPermissions } from 'api/user-permissions-slice';

import { GrantAccessModal } from '../GrantAccessModal/GrantAccessModal';
import styles from './SupportAccessMenu.module.scss';

export const SupportAccessMenu = () => {
  const wrapperRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [showGrantModal, setShowGrantModal] = useState(false);

  const permissions = useSelector(selectPermissions);
  const canManageCompany = permissions.includes(Permissions.CompanyWrite);

  const { data: pendingRequests, refetch: refetchPending } =
    useGetPendingSupportAccessRequestsQuery(undefined, { skip: !canManageCompany });
  const { data: activeRequests, refetch: refetchActive } = useGetActiveSupportAccessRequestsQuery(
    undefined,
    { skip: !canManageCompany },
  );

  const [grantRequest, { isLoading: granting }] = useGrantSupportAccessRequestMutation();
  const [denyRequest, { isLoading: denying }] = useDenySupportAccessRequestMutation();
  const [revokeRequest, { isLoading: revoking }] = useRevokeSupportAccessRequestMutation();

  useClickOutside(wrapperRef, () => setOpen(false));

  const activeRequest = activeRequests?.[0];
  const pendingRequest = pendingRequests?.[0];
  const request = activeRequest || pendingRequest;
  const isActive = !!activeRequest;

  if (!request) return null;

  const refetch = () => {
    refetchPending();
    refetchActive();
  };

  const handleGrant = async () => {
    await grantRequest(request.id).unwrap();
    refetch();
    setOpen(false);
    setShowGrantModal(false);
  };

  const handleDeny = async () => {
    await denyRequest(request.id).unwrap();
    refetch();
    setOpen(false);
    setShowGrantModal(false);
  };

  const handleRevoke = async () => {
    await revokeRequest(request.id).unwrap();
    refetch();
    setOpen(false);
    setShowRevokeConfirm(false);
  };

  return (
    <div className={styles.root} ref={wrapperRef}>
      <button
        className={cn(styles.trigger, { [styles.triggerActive]: isActive })}
        onClick={() => setOpen(!open)}>
        <HugeiconsIcon
          icon={CustomerSupportIcon}
          size={15}
          strokeWidth={1.5}
          color="currentColor"
        />
        <span>{isActive ? 'Active' : 'Pending'}</span>
        <HugeiconsIcon
          icon={ArrowDown01Icon}
          size={13}
          strokeWidth={2}
          color="currentColor"
          className={cn({ [styles.chevronOpen]: open })}
        />
      </button>

      {open && (
        <div className={cn(styles.dropdown, 'border border-border-primary')}>
          <div className={styles.dropdownHeader}>
            <p
              className={cn('t-body u-font-weight-600', {
                '!text-text-success': isActive,
                '!text-[#554800]': !isActive,
              })}>
              {isActive ? 'Access Active' : 'Access Pending'}
            </p>
            <p className="t-small secondary-text">
              {isActive
                ? 'Expires ' + moment(request.expires_at).format('D MMMM YYYY, h:mma')
                : 'Requested ' + moment(request.created_at).format('D MMMM YYYY, h:mma')}
            </p>
          </div>

          <div className={styles.actions}>
            {!isActive && (
              <button
                className={styles.actionItem}
                onClick={() => {
                  setShowGrantModal(true);
                  setOpen(false);
                }}
                disabled={granting || denying}>
                <HugeiconsIcon
                  icon={ShieldKeyIcon}
                  size={16}
                  strokeWidth={1.5}
                  color="currentColor"
                />
                Grant Access
              </button>
            )}
            <button
              className={styles.actionItem}
              onClick={() => {
                setShowRequestModal(true);
                setOpen(false);
              }}>
              <HugeiconsIcon icon={EyeIcon} size={16} strokeWidth={1.5} color="currentColor" />
              View Request
            </button>
            {isActive ? (
              <button
                className={cn(styles.actionItem, styles.danger)}
                onClick={() => {
                  setShowRevokeConfirm(true);
                  setOpen(false);
                }}
                disabled={revoking}>
                <HugeiconsIcon
                  icon={Delete01Icon}
                  size={16}
                  strokeWidth={1.5}
                  color="currentColor"
                />
                Revoke Access
              </button>
            ) : (
              <button
                className={cn(styles.actionItem, styles.danger)}
                onClick={handleDeny}
                disabled={granting || denying}>
                <HugeiconsIcon
                  icon={Delete01Icon}
                  size={16}
                  strokeWidth={1.5}
                  color="currentColor"
                />
                Deny Access
              </button>
            )}
          </div>
        </div>
      )}

      <AnimatedModal
        visible={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        icon={CustomerSupportIcon}
        oldIconLibrary={false}
        title="Support Access Request"
        small>
        <div className="flex flex-col gap-3">
          {request.reason && (
            <div>
              <p className="t-small u-font-weight-600 secondary-text">Reason</p>
              <p className="t-small">{request.reason}</p>
            </div>
          )}
          {request.ticket_number && (
            <div>
              <p className="t-small u-font-weight-600 secondary-text">Ticket</p>
              <p className="t-small">{request.ticket_number}</p>
            </div>
          )}
          <div>
            <p className="t-small u-font-weight-600 secondary-text">Duration</p>
            <p className="t-small">
              {request.duration_hours + ' hour' + (request.duration_hours !== 1 ? 's' : '')}
            </p>
          </div>
          <div>
            <p className="t-small u-font-weight-600 secondary-text">Requested by</p>
            <p className="t-small">{'@' + request.requested_by_name}</p>
          </div>
        </div>
      </AnimatedModal>

      <Alert
        show={showRevokeConfirm}
        title="Revoke Access"
        message="Are you sure you want to revoke support access? Checkmate support will immediately lose access to your account."
        icon={Delete01Icon}
        type="error"
        ok="Revoke Access"
        cancel="Cancel"
        onOk={handleRevoke}
        onCancel={() => setShowRevokeConfirm(false)}
      />

      <GrantAccessModal
        visible={showGrantModal}
        request={request}
        granting={granting}
        denying={denying}
        onClose={() => setShowGrantModal(false)}
        onDeny={handleDeny}
        onGrant={handleGrant}
      />
    </div>
  );
};
